/**
 * Dialog — shared types, Zod prop schema, and styling tokens.
 *
 * A modal confirmation: title, an always-visible close button, body content,
 * a primary action and an optional secondary action. Two presentations share
 * that one content contract — `centered` (a floating card) and `sheet`
 * (anchored to the bottom edge with a grabber). The host chooses the
 * presentation; the component does not read the viewport.
 *
 * For confirmations only. Help text and unit explanations expand in place
 * (ExpandableCallout, InfoDot), never in a dialog.
 *
 * Web only — the native build renders nothing.
 *
 * Source of truth: web registration v7 sheet, Web/Dialog
 * https://www.figma.com/design/hk9NaJrKRwkoGmmZ5rxbsE (node 49:5525)
 */

import { z } from 'zod';
import { actionIdSchema } from '@galapagos/product-analytics';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { shadows } from '../../elements/shadows';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';
import { typography } from '../../elements/typography';
import type { IconName } from '../../elements/icons/registry';
import { GRABBER_HEIGHT, GRABBER_RADIUS, GRABBER_WIDTH } from '../SmallSheet/SmallSheet';

export const DialogPresentation = z.enum(['centered', 'sheet']);
export type DialogPresentation = z.infer<typeof DialogPresentation>;

/** Catalog-facing schema (plain ZodObject so `formatZodType` can introspect it). */
export const dialogPropsSchema = z.object({
  open: z.boolean().default(false),
  presentation: DialogPresentation.default('centered'),
  title: z.string().optional(),
  /** Accessible name of the dialog when it has no visible title. */
  accessibilityLabel: z.string().optional(),
  /** Accessible name of the close button. Required: the button shows only an icon. */
  closeLabel: z.string().min(1),
  primaryActionLabel: z.string().optional(),
  secondaryActionLabel: z.string().optional(),
  /** Analytics ids; omitted ones fall back to the generic `ui.dialog.*` defaults. */
  actionId: actionIdSchema.optional(),
  primaryActionId: actionIdSchema.optional(),
  secondaryActionId: actionIdSchema.optional(),
  testID: z.string().optional(),
});

/** Validation-only schema: a modal must have an accessible name. */
export const dialogPropsRefinedSchema = dialogPropsSchema.refine(
  (data) => Boolean(data.title) || Boolean(data.accessibilityLabel),
  { message: 'Dialog needs a title or an accessibilityLabel', path: ['title'] },
);

export type DialogProps = z.input<typeof dialogPropsSchema>;

export const DIALOG_BACKGROUND = colors.background.container;
export const DIALOG_BACKDROP = colors.background.overlay;
export const DIALOG_TEXT_COLOR = colors.text.primary;
export const DIALOG_TITLE_TYPOGRAPHY = typography.h2Bold;
export const DIALOG_BODY_TYPOGRAPHY = typography.b2Regular;
/** Space between the body and the actions, on top of the stack gap. */
export const DIALOG_ACTIONS_SPACER = spacing.s;
/** Gap between the title and the close button. */
export const DIALOG_HEADER_GAP = spacing.m;

export const DIALOG_CENTERED_PADDING = spacing.xl;
export const DIALOG_CENTERED_GAP = spacing.m;
export const DIALOG_CENTERED_RADIUS = radius.xxl;
export const DIALOG_CENTERED_SHADOW = shadows.shadow500;
/** The centred dialog fills the viewport up to this width. */
export const DIALOG_CENTERED_MAX_WIDTH = sizing.dialog.maxWidth;

export const DIALOG_SHEET_PADDING_TOP = spacing.s;
export const DIALOG_SHEET_PADDING_HORIZONTAL = spacing.lg;
export const DIALOG_SHEET_PADDING_BOTTOM = spacing.xl;
export const DIALOG_SHEET_GAP = spacing.sm;
export const DIALOG_SHEET_RADIUS = radius.xxxl;
export const DIALOG_GRABBER_WIDTH = GRABBER_WIDTH;
export const DIALOG_GRABBER_HEIGHT = GRABBER_HEIGHT;
export const DIALOG_GRABBER_RADIUS = GRABBER_RADIUS;
export const DIALOG_GRABBER_COLOR = colors.button.disabled;

export const DIALOG_CLOSE_SIZE = sizing.controlButton.md;
export const DIALOG_CLOSE_BACKGROUND = colors.button.secondary;
export const DIALOG_CLOSE_RADIUS = radius.rounded;
export const DIALOG_CLOSE_ICON: IconName = 'close';
export const DIALOG_CLOSE_ICON_SIZE = sizing.icon.xs;
export const DIALOG_CLOSE_ICON_COLOR = colors.icon.primary;
